import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import Link from "@mui/material/Link";
import StarIcon from "@mui/icons-material/StarBorder";
import { useTheme } from "@mui/material";
import { LucideCheck } from "lucide-react";
import ModalBasicLayout from "../Modal/ModalBasicLayout";
import Button from "../Buttons/Button";
import TitlePage from "../Texts/TitlePage";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import {
  FEATURE_ADDON_KEYS,
  startPreviewFeatureAddonProration,
} from "../../actions/featureAddons";
import {
  FEATURE_ADDON_COMPETITOR_ACTION,
  FEATURE_ADDON_FREE_TIER_ID,
  getPopularAddonTierId,
} from "../../utils/featureAddonCatalog";
import { maxPostsPerWeek } from "../../utils/publishingAutomationFrequency";
import { WHITE_LABEL_TIER_ID } from "../../utils/whiteLabelAddon";
import {
  companyInformation,
  LINK_LANDING_CONTACT,
} from "../../utils/constants";
import { BLACK_COLOR_BORDER, INPUT_COLOR_BORDER } from "../../utils/colors";
import {
  BUTTON_YOU_HAVE_DOUBTS,
  FEATURE_ADDON_BENEFIT_AUTOMATIONS_FREQUENCY,
  FEATURE_ADDON_BENEFIT_AUTOMATIONS_PLATFORMS,
  FEATURE_ADDON_CLARIFICATION_MULTIPLATFORM_BODY,
  FEATURE_ADDON_CLARIFICATION_MULTIPLATFORM_TERM,
  FEATURE_ADDON_BENEFIT_AUTOMATIONS_SCHEDULE,
  FEATURE_ADDON_BENEFIT_CAPACITY_AUTOMATIONS,
  FEATURE_ADDON_BENEFIT_CAPACITY_AUTOMATIONS_ONE,
  FEATURE_ADDON_BENEFIT_CAPACITY_TEMPLATES,
  FEATURE_ADDON_BENEFIT_CAPACITY_TEMPLATES_ONE,
  FEATURE_ADDON_BENEFIT_COMPETITORS_FAVORITES,
  FEATURE_ADDON_BENEFIT_COMPETITORS_FAVORITES_ONE,
  FEATURE_ADDON_BENEFIT_COMPETITORS_GENERATE,
  FEATURE_ADDON_BENEFIT_COMPETITORS_GENERATE_ONE,
  FEATURE_ADDON_BENEFIT_COMPETITORS_PLATFORMS,
  FEATURE_ADDON_BENEFIT_COMPETITORS_POOL,
  FEATURE_ADDON_BENEFIT_COMPETITORS_POOL_ONE,
  FEATURE_ADDON_BENEFIT_TEMPLATES_LAYERS,
  FEATURE_ADDON_BENEFIT_TEMPLATES_PRODUCE,
  FEATURE_ADDON_BENEFIT_VISIBILITY,
  FEATURE_ADDON_BENEFIT_VISIBILITY_ALL,
  FEATURE_ADDON_HERO_AUTOMATIONS,
  FEATURE_ADDON_HERO_COMPETITORS,
  FEATURE_ADDON_HERO_TEMPLATES,
  FEATURE_ADDON_INCLUDED_AUTOMATIONS,
  FEATURE_ADDON_INCLUDED_COMPETITORS,
  FEATURE_ADDON_INCLUDED_TEMPLATES,
  FEATURE_ADDON_PAYWALL_FREE,
  FEATURE_ADDON_PAYWALL_INCLUDED,
  FEATURE_ADDON_PAYWALL_PER_MONTH,
  FEATURE_ADDON_PAYWALL_TIER_CTA,
  FEATURE_ADDON_PAYWALL_TIER_NAME,
  LABEL_CURRENT_SUBSCRIPTION_PLAN,
  WHITE_LABEL_AFTER_PAYMENT,
  WHITE_LABEL_DIALOG_BODY,
  WHITE_LABEL_NO_SETUP_FEE,
  WHITE_LABEL_TITLE,
  MOST_POPULAR,
  VALUE_SUBSCRIPTION_AFTER_PRORATED,
  VALUE_SUBSCRIPTION_PRORATED,
} from "../../i18n/keysTranslations";

const HERO_KEY_BY_ADDON = {
  [FEATURE_ADDON_KEYS.AUTOMATIONS]: FEATURE_ADDON_HERO_AUTOMATIONS,
  [FEATURE_ADDON_KEYS.TEMPLATES]: FEATURE_ADDON_HERO_TEMPLATES,
  [FEATURE_ADDON_KEYS.COMPETITORS]: FEATURE_ADDON_HERO_COMPETITORS,
  [FEATURE_ADDON_KEYS.WHITE_LABEL]: WHITE_LABEL_TITLE,
};

const CLARIFICATIONS_BY_ADDON = {
  [FEATURE_ADDON_KEYS.AUTOMATIONS]: [
    {
      termKey: FEATURE_ADDON_CLARIFICATION_MULTIPLATFORM_TERM,
      bodyKey: FEATURE_ADDON_CLARIFICATION_MULTIPLATFORM_BODY,
    },
  ],
};

const addonClarifications = (addonKey) =>
  CLARIFICATIONS_BY_ADDON[addonKey] || [];

const joinTranslated = (values, t) =>
  (Array.isArray(values) ? values : [])
    .map((value) => t(value))
    .filter(Boolean)
    .join(", ");

const tierBenefits = ({ tier, addonKey, t }) => {
  const limit = tier?.limit;
  const lines = [];
  if (addonKey === FEATURE_ADDON_KEYS.AUTOMATIONS) {
    lines.push(
      Number(limit) === 1
        ? t(FEATURE_ADDON_BENEFIT_CAPACITY_AUTOMATIONS_ONE)
        : t(FEATURE_ADDON_BENEFIT_CAPACITY_AUTOMATIONS, { limit })
    );
    const platforms = joinTranslated(tier?.platforms, t);
    if (platforms) {
      lines.push(t(FEATURE_ADDON_BENEFIT_AUTOMATIONS_PLATFORMS, { platforms }));
    }
    const postsPerWeek = maxPostsPerWeek(tier?.frequencies);
    if (postsPerWeek) {
      lines.push(
        t(FEATURE_ADDON_BENEFIT_AUTOMATIONS_FREQUENCY, { count: postsPerWeek })
      );
    }
    lines.push(t(FEATURE_ADDON_BENEFIT_AUTOMATIONS_SCHEDULE));
  } else if (addonKey === FEATURE_ADDON_KEYS.TEMPLATES) {
    lines.push(
      Number(limit) === 1
        ? t(FEATURE_ADDON_BENEFIT_CAPACITY_TEMPLATES_ONE)
        : t(FEATURE_ADDON_BENEFIT_CAPACITY_TEMPLATES, { limit })
    );
    if (tier?.produceLimit != null) {
      lines.push(
        t(FEATURE_ADDON_BENEFIT_TEMPLATES_PRODUCE, { limit: tier.produceLimit })
      );
    }
    lines.push(t(FEATURE_ADDON_BENEFIT_TEMPLATES_LAYERS));
  } else if (addonKey === FEATURE_ADDON_KEYS.COMPETITORS) {
    lines.push(
      tier?.id === FEATURE_ADDON_FREE_TIER_ID
        ? t(FEATURE_ADDON_BENEFIT_VISIBILITY, {
            limit: tier?.visibilityLimit ?? 15,
          })
        : t(FEATURE_ADDON_BENEFIT_VISIBILITY_ALL)
    );
    const platforms = joinTranslated(tier?.platforms, t);
    if (platforms) {
      lines.push(t(FEATURE_ADDON_BENEFIT_COMPETITORS_PLATFORMS, { platforms }));
    }
    const actions = Array.isArray(tier?.actions) ? tier.actions : [];
    const canGenerate = actions.includes(
      FEATURE_ADDON_COMPETITOR_ACTION.GENERATE
    );
    const favoriteLimit = Number(tier?.favoriteLimit);
    const generateLimit = Number(tier?.generateLimit);
    const splitQuotas =
      Number.isFinite(favoriteLimit) && Number.isFinite(generateLimit);
    if (splitQuotas) {
      lines.push(
        favoriteLimit === 1
          ? t(FEATURE_ADDON_BENEFIT_COMPETITORS_FAVORITES_ONE)
          : t(FEATURE_ADDON_BENEFIT_COMPETITORS_FAVORITES, {
              limit: favoriteLimit,
            })
      );
      if (canGenerate) {
        lines.push(
          generateLimit === 1
            ? t(FEATURE_ADDON_BENEFIT_COMPETITORS_GENERATE_ONE)
            : t(FEATURE_ADDON_BENEFIT_COMPETITORS_GENERATE, {
                limit: generateLimit,
              })
        );
      }
    } else if (canGenerate) {
      lines.push(t(FEATURE_ADDON_BENEFIT_COMPETITORS_GENERATE));
      lines.push(
        Number(limit) === 1
          ? t(FEATURE_ADDON_BENEFIT_COMPETITORS_POOL_ONE)
          : t(FEATURE_ADDON_BENEFIT_COMPETITORS_POOL, { limit })
      );
    } else if (Number(limit) === 1) {
      lines.push(t(FEATURE_ADDON_BENEFIT_COMPETITORS_FAVORITES_ONE));
    } else {
      lines.push(t(FEATURE_ADDON_BENEFIT_COMPETITORS_FAVORITES, { limit }));
    }
  } else if (addonKey === FEATURE_ADDON_KEYS.WHITE_LABEL) {
    lines.push(t(WHITE_LABEL_DIALOG_BODY));
    lines.push(t(WHITE_LABEL_NO_SETUP_FEE));
    lines.push(t(WHITE_LABEL_AFTER_PAYMENT));
  }
  return lines;
};

const includedClause = ({ tier, addonKey, t }) => {
  if (addonKey === FEATURE_ADDON_KEYS.AUTOMATIONS) {
    const capacity =
      Number(tier?.limit) === 1
        ? t(FEATURE_ADDON_BENEFIT_CAPACITY_AUTOMATIONS_ONE)
        : t(FEATURE_ADDON_BENEFIT_CAPACITY_AUTOMATIONS, { limit: tier?.limit });
    return t(FEATURE_ADDON_INCLUDED_AUTOMATIONS, {
      capacity,
      platforms: joinTranslated(tier?.platforms, t),
      count: maxPostsPerWeek(tier?.frequencies) || 0,
    });
  }
  if (addonKey === FEATURE_ADDON_KEYS.TEMPLATES) {
    const capacity =
      Number(tier?.limit) === 1
        ? t(FEATURE_ADDON_BENEFIT_CAPACITY_TEMPLATES_ONE)
        : t(FEATURE_ADDON_BENEFIT_CAPACITY_TEMPLATES, { limit: tier?.limit });
    return t(FEATURE_ADDON_INCLUDED_TEMPLATES, {
      capacity,
      produceLimit: tier?.produceLimit,
    });
  }
  if (addonKey === FEATURE_ADDON_KEYS.COMPETITORS) {
    return t(FEATURE_ADDON_INCLUDED_COMPETITORS, {
      visibility: tier?.visibilityLimit ?? 15,
      platforms: joinTranslated(tier?.platforms, t),
    });
  }
  return "";
};

const tierLabel = ({ tier, t }) => {
  if (tier?.id === FEATURE_ADDON_FREE_TIER_ID) {
    return t(FEATURE_ADDON_PAYWALL_FREE);
  }
  if (tier?.id === WHITE_LABEL_TIER_ID) {
    return t(WHITE_LABEL_TITLE);
  }
  const match = String(tier?.id || "").match(/(\d+)$/);
  return t(FEATURE_ADDON_PAYWALL_TIER_NAME, {
    index: match ? match[1] : tier?.id,
  });
};

const TierPrice = ({ tier, proration, isLoadingProration, t }) => {
  if (isLoadingProration) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 1,
        }}
      >
        <Skeleton variant="text" width={120} height={28} />
        <Skeleton variant="text" width={160} height={20} />
      </Box>
    );
  }

  if (proration && Number.isFinite(Number(proration.price))) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 1,
        }}
      >
        <Typography sx={{ fontWeight: 500, fontSize: "18px", mb: 0.5 }}>
          {t(VALUE_SUBSCRIPTION_PRORATED, {
            value: proration.price,
            currency: proration.currency || "USD",
          })}
        </Typography>
        {proration.monthlyPrice != null && proration.renewDate ? (
          <Typography sx={{ fontSize: "14px", textAlign: "center" }}>
            {t(VALUE_SUBSCRIPTION_AFTER_PRORATED, {
              value: proration.monthlyPrice,
              currency: proration.currency || "USD",
              date: new Date(proration.renewDate),
            })}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: 0.5,
        mt: 1,
      }}
    >
      <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1 }}>
        {`$${Number(tier.priceMonthly) || 0}`}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.4 }}>
        {t(FEATURE_ADDON_PAYWALL_PER_MONTH)}
      </Typography>
    </Box>
  );
};

const TierCard = ({
  tier,
  addonKey,
  isCurrent,
  isPopular,
  isUpgrade,
  isDowngrade,
  proration,
  isLoadingProration,
  isCheckingOut,
  checkingOutTierId,
  onCheckoutTier,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const isFree = tier?.id === FEATURE_ADDON_FREE_TIER_ID;
  const benefits = tierBenefits({ tier, addonKey, t });

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 220px",
        minWidth: 220,
        maxWidth: 260,
        width: "100%",
        mt: { xs: isPopular ? 2 : 0, sm: 0 },
      }}
    >
      {isPopular && (
        <Box
          sx={{
            position: "absolute",
            top: -15,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            background: theme.palette.button.primary.main,
            color: theme.palette.button.primary.contrastText,
            borderRadius: "10px",
            px: 2,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
          }}
        >
          <StarIcon sx={{ color: "white !important", fontSize: "18px" }} />
          <Typography
            sx={{
              color: "white !important",
              fontSize: "10px",
              fontWeight: 600,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            {t(MOST_POPULAR)}
          </Typography>
        </Box>
      )}
      <Card
        sx={{
          backgroundColor: "background.paper",
          border: `1px solid ${isPopular ? BLACK_COLOR_BORDER : INPUT_COLOR_BORDER}`,
          borderRadius: 3,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxShadow: isPopular
            ? "0px 8px 24px rgba(0,0,0,0.15)"
            : "0px 2px 8px rgba(0,0,0,0.1)",
          overflow: "visible",
        }}
      >
        <Box sx={{ px: 2, pt: 3, pb: 1, textAlign: "center" }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontStyle: "italic",
              fontSize: "20px",
            }}
          >
            {tierLabel({ tier, t })}
          </Typography>
          <TierPrice
            tier={tier}
            proration={isUpgrade ? proration : null}
            isLoadingProration={isUpgrade && isLoadingProration}
            t={t}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            px: 2,
            py: 2,
            flex: 1,
          }}
        >
          {benefits.map((line) => (
            <Box
              key={line}
              sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
            >
              <LucideCheck
                size={18}
                color="#4CAF50"
                style={{ marginTop: 1, flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.4, fontSize: "14px" }}
              >
                {line}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ px: 2, pb: 2, pt: 0 }}>
          {isCurrent ? (
            <Button fullWidth disabled color="primary">
              {t(LABEL_CURRENT_SUBSCRIPTION_PLAN)}
            </Button>
          ) : isFree || isDowngrade ? (
            <Box sx={{ minHeight: 36.5 }} />
          ) : (
            <Button
              fullWidth
              color="primary"
              loading={isCheckingOut && checkingOutTierId === tier.id}
              disabled={isCheckingOut || (isUpgrade && isLoadingProration)}
              onClick={() => onCheckoutTier(tier.id)}
            >
              {t(FEATURE_ADDON_PAYWALL_TIER_CTA)}
            </Button>
          )}
        </Box>
      </Card>
    </Box>
  );
};

const FeatureAddonPaywallDialog = ({
  open,
  onClose,
  status,
  addonKey,
  isLoading,
  isCheckingOut,
  onCheckoutTier,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [prorationByTier, setProrationByTier] = useState(null);
  const [isLoadingProration, setIsLoadingProration] = useState(false);
  const [checkingOutTierId, setCheckingOutTierId] = useState(null);
  const tiers = [...(status?.tiers || [])].sort((a, b) => {
    if (a?.id === FEATURE_ADDON_FREE_TIER_ID) return -1;
    if (b?.id === FEATURE_ADDON_FREE_TIER_ID) return 1;
    return 0;
  });
  const freeTier = tiers.find((tier) => tier.id === FEATURE_ADDON_FREE_TIER_ID);
  const paidTiers = tiers.filter(
    (tier) => tier.id !== FEATURE_ADDON_FREE_TIER_ID
  );
  const currentTierId = status?.effective?.tierId || FEATURE_ADDON_FREE_TIER_ID;
  const hasPaidSubscription =
    status?.agencyAddon?.isActive === true &&
    currentTierId !== FEATURE_ADDON_FREE_TIER_ID;
  const catalogRank = (tierId) =>
    (status?.tiers || []).findIndex((tier) => tier.id === tierId);
  const currentRank = catalogRank(currentTierId);
  const popularTierId = getPopularAddonTierId(tiers);

  useEffect(() => {
    if (!open || !addonKey || !hasPaidSubscription) {
      setProrationByTier(null);
      setIsLoadingProration(false);
      return undefined;
    }
    let cancelled = false;
    setIsLoadingProration(true);
    dispatch(startPreviewFeatureAddonProration({ addonKey })).then((result) => {
      if (cancelled) return;
      setProrationByTier(result?.ok ? result.data?.upgrades || {} : {});
      setIsLoadingProration(false);
    });
    return () => {
      cancelled = true;
    };
  }, [addonKey, dispatch, hasPaidSubscription, open]);

  useEffect(() => {
    if (!open || !isCheckingOut) setCheckingOutTierId(null);
  }, [isCheckingOut, open]);

  const handleCheckoutTier = (tierId) => {
    setCheckingOutTierId(tierId);
    return onCheckoutTier(tierId);
  };
  const heroKey = HERO_KEY_BY_ADDON[addonKey];
  const heroTitle = heroKey ? t(heroKey) : "";
  const clarifications = addonClarifications(addonKey);
  const includedBenefits = freeTier
    ? includedClause({ tier: freeTier, addonKey, t })
    : "";
  const includedLabel = includedBenefits
    ? t(FEATURE_ADDON_PAYWALL_INCLUDED, { benefits: includedBenefits })
    : "";

  return (
    <ModalBasicLayout
      modalOpen={open}
      onCloseModal={onClose}
      title=""
      description=""
      maxWidth="lg"
      fullWidth
      ariaLabel={heroTitle}
    >
      <TitlePage
        align="center"
        sxContainer={{ justifyContent: "center", width: "100%" }}
        sx={{
          textAlign: "center",
          mb: 1,
          px: { xs: 1, sm: 4 },
        }}
      >
        {heroTitle}
      </TitlePage>
      {includedLabel ? (
        <Typography
          align="center"
          sx={{
            px: { xs: 1, sm: 4 },
            mb: 1,
            color: "text.secondary",
            fontSize: "15px",
            lineHeight: 1.5,
          }}
        >
          {includedLabel}
        </Typography>
      ) : null}
      {isLoading && paidTiers.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "stretch",
            gap: 2,
            pt: 3,
            pb: 1,
          }}
        >
          {paidTiers.map((tier) => {
            const rank = catalogRank(tier.id);
            return (
              <TierCard
                key={tier.id}
                tier={tier}
                addonKey={addonKey}
                isCurrent={tier.id === currentTierId}
                isPopular={tier.id === popularTierId}
                isUpgrade={hasPaidSubscription && rank > currentRank}
                isDowngrade={
                  hasPaidSubscription && rank >= 0 && rank < currentRank
                }
                proration={prorationByTier?.[tier.id] || null}
                isLoadingProration={
                  isLoadingProration && !prorationByTier?.[tier.id]
                }
                isCheckingOut={isCheckingOut}
                checkingOutTierId={checkingOutTierId}
                onCheckoutTier={handleCheckoutTier}
              />
            );
          })}
        </Box>
      )}
      {clarifications.length > 0 ? (
        <Box
          sx={{
            mt: 2,
            mx: { xs: 1, sm: 4 },
            pt: 2,
            borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {clarifications.map((item) => (
            <Typography
              key={item.termKey}
              align="center"
              sx={{
                maxWidth: 640,
                color: "text.secondary",
                fontSize: "14px",
                lineHeight: 1.5,
              }}
            >
              <Box
                component="span"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {t(item.termKey)}.
              </Box>{" "}
              {t(item.bodyKey)}
            </Typography>
          ))}
        </Box>
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 3,
          mb: 1,
          gap: 1,
        }}
      >
        <Link
          href={LINK_LANDING_CONTACT}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{
            color: "text.primary",
            fontSize: "1.1rem",
            textDecoration: "underline",
          }}
        >
          {t(BUTTON_YOU_HAVE_DOUBTS)}
        </Link>
        <Typography variant="body2" align="center">
          {`${companyInformation.name} - A Delaware LLC`}
        </Typography>
        <Typography variant="body2" align="center">
          {companyInformation.address}
        </Typography>
      </Box>
    </ModalBasicLayout>
  );
};

FeatureAddonPaywallDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  status: PropTypes.object,
  addonKey: PropTypes.string,
  isLoading: PropTypes.bool,
  isCheckingOut: PropTypes.bool,
  onCheckoutTier: PropTypes.func.isRequired,
};

TierPrice.propTypes = {
  tier: PropTypes.object.isRequired,
  proration: PropTypes.object,
  isLoadingProration: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

TierCard.propTypes = {
  tier: PropTypes.object.isRequired,
  addonKey: PropTypes.string,
  isCurrent: PropTypes.bool,
  isPopular: PropTypes.bool,
  isUpgrade: PropTypes.bool,
  isDowngrade: PropTypes.bool,
  proration: PropTypes.object,
  isLoadingProration: PropTypes.bool,
  isCheckingOut: PropTypes.bool,
  checkingOutTierId: PropTypes.string,
  onCheckoutTier: PropTypes.func.isRequired,
};

export default FeatureAddonPaywallDialog;
